// ABOUTME: Tracks the case consent alone cannot see: voice consent given, but the BROWSER refused the microphone (prompt
// ABOUTME: dismissed or blocked, no device). Drives the VoiceOffPrompt so the guest is one tap from voice, or told where to allow it.

import { useCallback, useEffect, useRef, useState } from 'react';
import { DEFAULT_CATEGORIES } from './consentConfig';

/** getUserMedia rejections that mean "the guest or the browser said no"; and the ones that mean there is no device at all. Anything
 *  else (another app holding the mic: NotReadableError; a graph that would not build) is `failed`. */
const DENIED = new Set(['NotAllowedError', 'SecurityError', 'PermissionDeniedError']);
const MISSING = new Set(['NotFoundError', 'OverconstrainedError', 'DevicesNotFoundError']);

/** The browser's own permission state for the mic, or null where the Permissions API cannot answer (older Firefox/Safari). */
async function queryMic() {
  try {
    if (!navigator.permissions?.query) return null;
    return await navigator.permissions.query({ name: 'microphone' });
  } catch {
    return null;
  }
}

/**
 * @param {object} p
 * @param {Function} p.onGranted called when the browser's permission flips to granted while blocked (allowed from site settings)
 * @returns {{ blocked: (null|'denied'|'unavailable'|'failed'), hardBlocked: boolean, live: boolean, setLive: Function, fail: Function, clear: Function, retried: boolean,
 *             markRetry: Function }}
 *   `hardBlocked`: the browser will not prompt again (state `denied`, or a retry that failed where the API cannot tell us)
 */
export default function useMicBlocked({ onGranted }) {
  const [blocked, setBlocked] = useState(null);
  const [permState, setPermState] = useState(null);
  const [retried, setRetried] = useState(false);
  const onGrantedRef = useRef(onGranted);
  onGrantedRef.current = onGranted;
  const blockedRef = useRef(null);

  const fail = useCallback((err) => {
    const kind = DENIED.has(err?.name) ? 'denied' : MISSING.has(err?.name) ? 'unavailable' : 'failed';
    blockedRef.current = kind;
    setBlocked(kind);
    setLive(false);
    console.warn(`[mic] blocked=${kind} (${err?.name || err})`);
  }, []);
  const clear = useCallback(() => {
    if (!blockedRef.current) return;
    blockedRef.current = null;
    setBlocked(null);
    setRetried(false);
  }, []);
  const markRetry = useCallback(() => setRetried(true), []);
  const clearRef = useRef(clear);
  clearRef.current = clear;
  // `live`: the capture graph is really running (App sets it where processorRef is built / torn down). The mic icon and the
  // recording dot follow THIS under mic_voice_control, never the guest's wish (isMicOn), which flickers on a refused restart.
  const [live, setLive] = useState(false);

  // While blocked, follow the browser's permission: `denied` means no prompt will come (point at site settings); a flip to
  // `granted` (the guest allowed it from the padlock) restarts capture without a reload.
  useEffect(() => {
    if (!blocked) return undefined;
    let status = null;
    let gone = false;
    const onChange = () => {
      setPermState(status.state);
      // Allowed in site settings: the notice goes at once (no in-between 'Enable microphone' frame while capture restarts); a
      // restart that still fails puts it back through fail().
      if (status.state === 'granted' && blockedRef.current) { clearRef.current(); onGrantedRef.current?.(); }
    };
    queryMic().then((s) => {
      if (gone || !s) return;
      status = s;
      setPermState(s.state);
      s.addEventListener?.('change', onChange);
    });
    return () => { gone = true; status?.removeEventListener?.('change', onChange); };
  }, [blocked]);

  const hardBlocked = blocked === 'denied' && (permState === 'denied' || (permState === null && retried));
  return { blocked, hardBlocked, fail, clear, retried, markRetry, live, setLive };
}

/** The consent record with voice switched on (the widget's / mic button's one tap); everything else as the guest left it. */
export const withVoice = (categories) => ({ ...DEFAULT_CATEGORIES, ...(categories || {}), necessary: true, audioRecording: true });

/**
 * The VoiceOffPrompt's props, derived in one place (App.js passes the facts, decides nothing):
 * - a returning guest's no_enter_gate landing: the widget IS the entry ('start'; with voice declined 'voice' = 'Turn on voice',
 *   whose one tap records voice consent and enters; a lapsed record gets the consent gate from the entry itself);
 * - in the boutique without voice consent: the original muted-concierge prompt opening the privacy panel - EXCEPT under
 *   mic_voice_control (dev + staging), where the mic button is the one voice control and no widget is shown;
 * - in the boutique, the BROWSER refusing the mic: the mic copy ('hard' = blocked: no button, only site settings can lift it).
 * Never while a piece card or a rail panel is up (it would sit over their close buttons).
 *
 * @param {object} p
 * @param {boolean} p.started isExperienceStarted
 * @param {{waiting: boolean, returning: boolean, tap: Function}} p.landing useLanding()
 * @param {boolean} p.hasAudioConsent
 * @param {boolean} [p.needsDecision] the consent record is missing or stale (the entry opens the consent gate itself)
 * @param {{blocked: (null|string), hardBlocked: boolean}} p.mic useMicBlocked()
 * @param {boolean} [p.covered] a piece card or a rail panel is open
 * @param {boolean} [p.micVoiceControl] the mic_voice_control toggle
 * @param {Function} p.enterWithVoice records voice consent and enters
 * @param {Function} p.openPrivacy opens the privacy panel (flag-off muted-concierge prompt)
 * @param {Function} p.retryMic asks the browser again
 * @returns {{ show: boolean, mic: (null|string), onEnable: Function }}
 */
export function voiceOffPromptProps({
  started, landing, hasAudioConsent, needsDecision = false, mic, covered = false, micVoiceControl = false,
  enterWithVoice, openPrivacy, retryMic,
}) {
  if (!started && landing.waiting && landing.returning) {
    if (hasAudioConsent || needsDecision) return { show: true, mic: 'start', onEnable: landing.tap };
    return { show: true, mic: 'voice', onEnable: () => landing.tap(enterWithVoice) };
  }
  if (!started || covered) return { show: false, mic: null, onEnable: retryMic };
  if (!hasAudioConsent) return micVoiceControl ? { show: false, mic: null, onEnable: retryMic } : { show: true, mic: null, onEnable: openPrivacy };
  if (!mic.blocked) return { show: false, mic: null, onEnable: retryMic };
  return { show: true, mic: mic.hardBlocked ? 'hard' : mic.blocked, onEnable: retryMic };
}
